import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  CheckSquare,
  Plus,
  Filter,
  Clock,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  Calendar,
  Users
} from 'lucide-react'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { taskService } from '@/services/taskService'
import { useAuth } from '@/contexts/Auth0Context'
import { useToast } from '@/contexts/ToastContext'
import CreateTaskModal from '@/components/tasks/CreateTaskModal'
import TaskDetailView from '@/components/tasks/TaskDetailView'
import GroupTaskView from '@/components/tasks/GroupTaskView'
import {
  Task,
  TaskStatus,
  TaskPriority,
  TaskStats
} from '@/types/task'

const TasksPage: React.FC = () => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth()
  const { showToast } = useToast()
  const [tasks, setTasks] = useState<Task[]>([])
  const [stats, setStats] = useState<TaskStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedStatus, setSelectedStatus] = useState<TaskStatus | 'all'>('all')
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [detailTask, setDetailTask] = useState<Task | null>(null)
  const [groupViewId, setGroupViewId] = useState<string | null>(null)
  const location = useLocation()
  const navigate = useNavigate()

  // Prevent duplicate API calls
  const fetchingTasksRef = useRef(false)
  const fetchingStatsRef = useRef(false)
  // Track if initial fetch has been done
  const initialFetchDoneRef = useRef(false)

  const fetchTasks = useCallback(async () => {
    // Prevent duplicate calls
    if (fetchingTasksRef.current) {
      return
    }

    try {
      fetchingTasksRef.current = true
      setLoading(true)
      const params =
        selectedStatus === 'all' ? undefined : { status: selectedStatus }
      const tasks = await taskService.getMyTasks(params)
      setTasks(tasks)
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to fetch tasks'
      })
    } finally {
      setLoading(false)
      fetchingTasksRef.current = false
    }
  }, [selectedStatus, showToast])

  const fetchStats = useCallback(async () => {
    // Prevent duplicate calls
    if (fetchingStatsRef.current) {
      return
    }

    try {
      fetchingStatsRef.current = true
      const statsData = await taskService.getTaskStats()
      // Ensure stats has all required fields with defaults
      if (statsData) {
        setStats({
          total: statsData.total ?? 0,
          pending: statsData.pending ?? 0,
          inProgress: statsData.inProgress ?? 0,
          underReview: statsData.underReview ?? 0,
          completed: statsData.completed ?? 0,
          overdue: statsData.overdue ?? 0,
          avgProgress: statsData.avgProgress ?? 0
        })
      }
    } catch (error) {
      console.error('Failed to fetch task stats:', error)
    } finally {
      fetchingStatsRef.current = false
    }
  }, [])

  // Only fetch when authenticated and auth is not loading
  useEffect(() => {
    if (authLoading || !isAuthenticated) {
      // Still loading auth or not authenticated, don't fetch yet
      if (!authLoading && !isAuthenticated) {
        setLoading(false)
      }
      return
    }

    fetchTasks()
    fetchStats()
    initialFetchDoneRef.current = true
  }, [authLoading, isAuthenticated, fetchTasks, fetchStats])

  const handleStatusChange = async (taskId: string, status: TaskStatus) => {
    try {
      await taskService.updateTaskStatus(taskId, { status })
      showToast({
        type: 'success',
        message: 'Task status updated'
      })
      fetchTasks()
      fetchStats()
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to update task status'
      })
    }
  }

  const handleCompleteTask = async (taskId: string) => {
    try {
      await taskService.completeTask(taskId)
      showToast({
        type: 'success',
        message: 'Task completed!'
      })
      fetchTasks()
      fetchStats()
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to complete task'
      })
    }
  }

  const handleTaskCreated = () => {
    fetchTasks()
    fetchStats()
  }

  // Deep-link support: /tasks/:taskId opens the task detail view
  useEffect(() => {
    if (authLoading || !isAuthenticated) return
    const match = location.pathname.match(/^\/tasks\/([^/]+)$/)
    if (!match) {
      setDetailTask(null)
      return
    }
    taskService
      .getTask(match[1])
      .then((task) => setDetailTask(task))
      .catch(() => {
        showToast({ type: 'error', message: 'Failed to load task' })
      })
  }, [location.pathname, authLoading, isAuthenticated, showToast])

  // Deep-link support: /tasks?create=1 opens the create task modal
  useEffect(() => {
    if (authLoading || !isAuthenticated || user?.role !== 'mentor') return
    const params = new URLSearchParams(location.search)
    if (params.get('create') !== '1') return
    setIsCreateModalOpen(true)
    navigate('/tasks', { replace: true })
  }, [location.search, authLoading, isAuthenticated, user?.role, navigate])

  const getStatusColor = (status: TaskStatus): string => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-100'
      case 'in_progress':
        return 'text-accent-700 bg-accent-100'
      case 'under_review':
        return 'text-yellow-600 bg-yellow-100'
      case 'pending':
        return 'text-gray-600 bg-gray-100'
      case 'cancelled':
        return 'text-red-600 bg-red-100'
      default:
        return 'text-gray-600 bg-gray-100'
    }
  }

  const getPriorityColor = (priority: TaskPriority): string => {
    switch (priority) {
      case 'critical':
        return 'text-red-600 bg-red-100'
      case 'high':
        return 'text-orange-600 bg-orange-100'
      case 'medium':
        return 'text-yellow-600 bg-yellow-100'
      case 'low':
        return 'text-green-600 bg-green-100'
      default:
        return 'text-gray-600 bg-gray-100'
    }
  }

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'No due date'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const isOverdue = (dueDate?: string): boolean => {
    if (!dueDate) return false
    return new Date(dueDate) < new Date()
  }

  // Determine what to render for empty state or task list
  const renderTasksContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      )
    }

    if (!tasks || tasks.length === 0) {
      return (
        <div className="text-center py-12">
          <CheckSquare className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <Card className="max-w-md mx-auto">
            <CardHeader>
              <h3 className="text-lg font-semibold">No Tasks Found</h3>
            </CardHeader>
            <CardBody>
              <p className="text-gray-600 mb-4">
                {selectedStatus === 'all'
                  ? "You don't have any tasks yet."
                  : `No tasks with status "${selectedStatus}".`}
              </p>
              {user?.role === 'mentor' && (
                <Button variant="primary" onClick={() => setIsCreateModalOpen(true)}>
                  Create Your First Task
                </Button>
              )}
            </CardBody>
          </Card>
        </div>
      )
    }

    return (
      <div className="space-y-4">
        {tasks.map((task) => (
          <Card key={task.id} className="hover:shadow-md transition-shadow">
            <CardBody>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {task.title}
                    </h3>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                        task.status
                      )}`}
                    >
                      {task.status.replace('_', ' ')}
                    </span>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(
                        task.priority
                      )}`}
                    >
                      {task.priority}
                    </span>
                    {task.groupId && (
                      <button
                        type="button"
                        onClick={() => setGroupViewId(task.groupId!)}
                        className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors"
                      >
                        <Users className="w-3 h-3" />
                        Group assignment
                      </button>
                    )}
                  </div>

                  <p className="text-gray-600 mb-3">{task.description}</p>

                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span
                        className={
                          isOverdue(task.dueDate) ? 'text-red-600' : ''
                        }
                      >
                        {formatDate(task.dueDate)}
                        {isOverdue(task.dueDate) && ' (Overdue)'}
                      </span>
                    </div>

                    <div className="flex items-center gap-1">
                      <TrendingUp className="w-4 h-4" />
                      <span>{task.progressPercentage}% Complete</span>
                    </div>

                    {task.estimatedHours && (
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        <span>{task.estimatedHours}h estimated</span>
                      </div>
                    )}
                  </div>

                  {task.tags && task.tags.length > 0 && (
                    <div className="flex gap-2 mt-3">
                      {task.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Progress Bar */}
                  <div className="mt-3">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-primary-600 h-2 rounded-full transition-all"
                        style={{ width: `${task.progressPercentage}%` }}
                      ></div>
                    </div>
                  </div>
                </div>

                <div className="ml-4 flex flex-col gap-2">
                  {user?.role === 'mentee' &&
                    task.status !== 'completed' &&
                    task.status !== 'cancelled' && (
                      <>
                        {task.status === 'pending' && (
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() =>
                              handleStatusChange(task.id, 'in_progress')
                            }
                          >
                            Start
                          </Button>
                        )}
                        {task.status === 'in_progress' && (
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() =>
                              handleStatusChange(task.id, 'under_review')
                            }
                          >
                            Submit for Review
                          </Button>
                        )}
                        {task.status === 'under_review' && (
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleCompleteTask(task.id)}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            Mark Complete
                          </Button>
                        )}
                      </>
                    )}
                </div>
              </div>
            </CardBody>
          </Card>
        ))}
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Tasks</h1>
            <p className="mt-2 text-gray-600">
              Manage your learning tasks and track progress
            </p>
          </div>
          {user?.role === 'mentor' && (
            <Button variant="primary" onClick={() => setIsCreateModalOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Task
            </Button>
          )}
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardBody>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Tasks</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {stats.total}
                    </p>
                  </div>
                  <CheckSquare className="w-8 h-8 text-gray-400" />
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">In Progress</p>
                    <p className="text-2xl font-bold text-accent-700">
                      {stats.inProgress}
                    </p>
                  </div>
                  <Clock className="w-8 h-8 text-accent-400" />
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Completed</p>
                    <p className="text-2xl font-bold text-green-600">
                      {stats.completed}
                    </p>
                  </div>
                  <CheckCircle2 className="w-8 h-8 text-green-400" />
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Overdue</p>
                    <p className="text-2xl font-bold text-red-600">
                      {stats.overdue}
                    </p>
                  </div>
                  <AlertCircle className="w-8 h-8 text-red-400" />
                </div>
              </CardBody>
            </Card>
          </div>
        )}

        {/* Filters */}
        <div className="mb-6">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <button
              onClick={() => setSelectedStatus('all')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                selectedStatus === 'all'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setSelectedStatus('pending')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                selectedStatus === 'pending'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              Pending
            </button>
            <button
              onClick={() => setSelectedStatus('in_progress')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                selectedStatus === 'in_progress'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              In Progress
            </button>
            <button
              onClick={() => setSelectedStatus('under_review')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                selectedStatus === 'under_review'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              Under Review
            </button>
            <button
              onClick={() => setSelectedStatus('completed')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                selectedStatus === 'completed'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              Completed
            </button>
          </div>
        </div>

        {/* Tasks List */}
        {renderTasksContent()}
      </div>

      {/* Create Task Modal */}
      <CreateTaskModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onTaskCreated={handleTaskCreated}
      />

      {detailTask && (
        <TaskDetailView
          task={detailTask}
          isOpen
          onClose={() => {
            setDetailTask(null)
            navigate('/tasks')
          }}
          onViewGroup={(groupId) => setGroupViewId(groupId)}
        />
      )}

      {groupViewId && (
        <GroupTaskView
          groupId={groupViewId}
          isOpen
          onClose={() => setGroupViewId(null)}
          onSelectTask={(task) => {
            setGroupViewId(null)
            setDetailTask(task)
          }}
          onGroupUpdated={() => {
            fetchTasks()
            fetchStats()
          }}
        />
      )}
    </div>
  )
}

export default TasksPage
